import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, take, timeout, withLatestFrom } from 'rxjs/operators';

import { AppSelectors } from '../../store/app/app.selectors';
import { UserProfileService } from '../settings/services/user-profile.service';
import { SemerisRoutes } from '../shared/constants/routes';
import { WORKSPACE_URL_NAMESPACE } from '../shared/constants/workspace-scoped-routes';
import { resolveAccountStatus } from '../shared/models/account-status.model';

/** Cap on the init wait, matching `workspaceUrlGuard`. */
const INIT_WAIT_TIMEOUT_MS = 10_000;

/**
 * Onboarding accounts get one workspace and nothing to set up, so `/setup` sends them to it. The `/ws/:id`
 * link claims it for the tab only: saving it would mark setup done before they have their real tenant.
 */
export const onboardingSetupGuard: CanActivateFn = () => {
  const store = inject(Store);
  const router = inject(Router);

  // A fresh profile, like `AssetGuard`'s: with the boot one, a move off onboarding since boot would bounce between them.
  return inject(UserProfileService)
    .getFreshOrLoadedProfile()
    .pipe(
      switchMap((profile) =>
        resolveAccountStatus(profile) !== 'onboarding'
          ? of(true)
          : store.select(AppSelectors.initialized).pipe(
              filter((initialized) => !!initialized),
              take(1),
              withLatestFrom(store.select(AppSelectors.availableWorkspaces)),
              map(([, workspaces]) => {
                const workspace = workspaces.find(({ id }) => id === profile.selectedWorkspaceId) ?? workspaces[0];

                return workspace ? router.createUrlTree(['/', WORKSPACE_URL_NAMESPACE, workspace.id, SemerisRoutes.home.root]) : true;
              }),
              timeout({ first: INIT_WAIT_TIMEOUT_MS, with: () => of(true) }),
            ),
      ),
      catchError(() => of(false)),
    );
};
